/**
 * General CSS used for ALL outgoing emails (preview + rendered templates).
 * Centralising it here means every mail template (and the GENERAL_HEADER /
 * GENERAL_FOOTER templates) shares the same base look: fixed content width,
 * consistent top padding, brand colors and table styling.
 *
 * If the backend email renderer builds its own HTML wrapper, keep it in sync
 * with this file (or expose this string via an API/shared constant) so the
 * preview always matches what is actually sent.
 */


export const EMAIL_CONTENT_MAX_WIDTH = "720px";
export const EMAIL_TOP_PADDING = "24px";
export const EMAIL_SIDE_PADDING = "16px";

export const EMAIL_BASE_STYLES = `
  html, body {
    margin: 0;
    padding: 0;
    background: #f4f4f4;
  }
  body {
    font-family: Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    padding-top: ${EMAIL_TOP_PADDING};
    padding-bottom: ${EMAIL_TOP_PADDING};
    padding-left: ${EMAIL_SIDE_PADDING};
    padding-right: ${EMAIL_SIDE_PADDING};
  }
  .email-content-wrapper {
    max-width: ${EMAIL_CONTENT_MAX_WIDTH};
    margin: 0 auto;
    background: #ffffff;
    padding: 24px;
    box-sizing: border-box;
  }
  h1, h2, h3, h4, h5, h6 {
    margin: 1em 0 0.5em;
    font-weight: bold;
    color: #1e3a5f;
  }
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.17em; }
  h4 { font-size: 1em; }
  h5 { font-size: 0.83em; }
  h6 { font-size: 0.67em; }
  p { margin: 0.5em 0; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  li { margin: 0.25em 0; }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }
  td, th {
    border: 1px solid #ddd;
    padding: 8px;
    word-break: break-word;
  }
  thead th {
    background: #1BACE4;
    color: #ffffff;
    font-weight: bold;
    text-align: left;
  }
  /* Don't override inline styles set by the table editor */
  td[style*="border"], th[style*="border"] { border: none; }
  td[style*="background-color"], th[style*="background-color"] { background: none; }
  a { color: #1BACE4; text-decoration: underline; }
  b, strong { font-weight: bold; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }
  s, strike { text-decoration: line-through; }
  code {
    background-color: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
  mark, .cdx-marker {
    background-color: #ffeb3b;
    padding: 2px 0;
  }
  figure { margin: 1em 0; text-align: center; }
  figure img { max-width: 100%; height: auto; }
  figcaption {
    font-size: 0.9em;
    color: #666;
    margin-top: 0.5em;
    font-style: italic;
  }
  blockquote {
    border-left: 3px solid #1BACE4;
    padding-left: 15px;
    margin: 1em 0;
    font-style: italic;
    color: #555;
  }
  blockquote cite {
    display: block;
    margin-top: 0.5em;
    font-size: 0.9em;
    color: #888;
  }
  hr {
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 2em 0;
  }
  .cdx-text-color__text { color: inherit; }
  span[style] { /* inline styles preserved */ }
  .tab { display: inline-block; width: 2em; }
`;

/**
 * Wrap raw email body HTML with the shared wrapper markup used by the
 * preview. Keep this in sync with EmailService::wrapInHtml() (PHP): the
 * table-based wrapper is required because Outlook's Word rendering engine
 * ignores max-width on divs but respects the width attribute on tables.
 */
export const wrapEmailHtml = (bodyHtml) => `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="color-scheme" content="light">
    <meta name="supported-color-schemes" content="light">
    <style>${EMAIL_BASE_STYLES}</style>
  </head>
  <body bgcolor="#f4f4f4" style="background: #f4f4f4;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#f4f4f4" style="background: #f4f4f4;">
      <tr>
        <td align="center" style="border: none; padding: ${EMAIL_TOP_PADDING} ${EMAIL_SIDE_PADDING};">
          <table role="presentation" class="email-content-wrapper" width="${parseInt(EMAIL_CONTENT_MAX_WIDTH, 10)}" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="width: 100%; max-width: ${EMAIL_CONTENT_MAX_WIDTH}; background: #ffffff;">
            <tr>
              <td bgcolor="#ffffff" style="border: none; background: #ffffff; padding: 24px;">
                ${bodyHtml}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
  </html>
`;
